import { AUTH_SCREEN } from "./url";

const PKCE_VERIFIER_STORAGE_KEY = "pkce_code_verifier";

/**
 * Generates a cryptographically random code verifier string (RFC 7636)
 */
export function generateCodeVerifier(length = 64) {
  const possible =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const values = new Uint8Array(length);
  window.crypto.getRandomValues(values);
  return Array.from(values)
    .map((x) => possible[x % possible.length])
    .join("");
}

/**
 * Generates base64url-encoded SHA-256 code challenge from a verifier string
 */
export async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);

  // Convert ArrayBuffer to base64url string
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
  return base64
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Initiates the PKCE OAuth login flow:
 * 1. Generates and stores code_verifier in sessionStorage
 * 2. Computes SHA-256 code_challenge
 * 3. Redirects to Auth Screen with code_challenge & code_challenge_method=S256
 */
export async function initiatePkceLogin(customRedirectUri) {
  const codeVerifier = generateCodeVerifier(64);
  sessionStorage.setItem(PKCE_VERIFIER_STORAGE_KEY, codeVerifier);

  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const redirectUri = customRedirectUri || window.location.origin;

  // Construct Auth URL
  const baseUrl = AUTH_SCREEN.startsWith("http")
    ? AUTH_SCREEN
    : window.location.origin + AUTH_SCREEN;

  const url = new URL(baseUrl);
  url.searchParams.set("code_challenge", codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("client_id", "application_client");
  url.searchParams.set("response_type", "code");
  url.searchParams.set("redirect", redirectUri);
  url.searchParams.set("redirect_uri", redirectUri);

  window.location.href = url.toString();
}

/**
 * Retrieves and optionally cleans up the stored code_verifier for token exchange
 */
export function getStoredCodeVerifier(removeAfterGet = true) {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_STORAGE_KEY);
  if (removeAfterGet) {
    sessionStorage.removeItem(PKCE_VERIFIER_STORAGE_KEY);
  }
  return verifier;
}
